import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { FileUploadZone, AttachmentList } from "@/components/FileUpload";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Users, Clock, Download, Loader2, FileText, CheckCircle, XCircle, 
  AlertCircle, Send, Paperclip, Eye, UserPlus, Edit, Trash2, Search
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContractorRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [responseDialog, setResponseDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [downloadingQuote, setDownloadingQuote] = useState(null);
  const [quoteStatusDialog, setQuoteStatusDialog] = useState(false);
  const [selectedQuoteRequest, setSelectedQuoteRequest] = useState(null);
  const [requestAttachments, setRequestAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // Quote Preview state
  const [quotePreviewDialog, setQuotePreviewDialog] = useState(false);
  const [quotePreview, setQuotePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [quoteWorkerType, setQuoteWorkerType] = useState("technical");
  const [responseData, setResponseData] = useState({
    status: "confirmed",
    contractor_response: ""
  });
  
  // PO filter state (read-only view for contractor)
  const [filterWithoutPO, setFilterWithoutPO] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Memoize filtered requests - universal search across all fields
  const filteredRequests = useMemo(() => {
    let result = requests;
    
    if (filterWithoutPO) {
      result = result.filter(r => !r.po_number);
    }
    
    if (searchQuery) {
      const search = searchQuery.toLowerCase().trim();
      result = result.filter(r => {
        // Search across multiple fields
        const searchableFields = [
          r.agreement_a_number,
          r.agreement_project_name,
          r.po_number,
          r.location_name,
          r.address,
          r.country,
          r.status,
          r.contact_person_onsite_name,
          r.description,
          r.notes,
          r.created_by_name,
          // Search in assigned workers array
          ...(r.assigned_worker_names || [])
        ];
        
        return searchableFields.some(field => 
          field && field.toLowerCase().includes(search)
        );
      });
    }
    
    return result;
  }, [requests, filterWithoutPO, searchQuery]);

  const fetchRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/worker-requests`);
      setRequests(response.data);
    } catch (error) {
      toast.error("Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWorkers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setWorkers(response.data);
    } catch (error) {
      console.error("Failed to load workers");
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchWorkers();
  }, [fetchRequests, fetchWorkers]);

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm("Permanently delete this request? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/worker-requests/${requestId}`);
      toast.success("Request deleted successfully");
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete request");
    }
  };

  const openViewDialog = async (request) => {
    setSelectedRequest(request);
    
    // Fetch all attachments (request + agreement level)
    try {
      const attachmentsRes = await axios.get(`${API}/worker-requests/${request.id}/all-attachments`);
      setRequestAttachments([
        ...(attachmentsRes.data.request_attachments || []),
        ...(attachmentsRes.data.agreement_attachments || [])
      ]);
    } catch (error) {
      console.error("Failed to load attachments");
      setRequestAttachments([]);
    }
    
    setViewDialog(true);
  };

  const openResponseDialog = async (request) => {
    setSelectedRequest(request);
    setResponseData({
      status: request.status === "pending" ? "confirmed" : request.status,
      contractor_response: request.contractor_response || ""
    });
    
    // Fetch attachments for this request
    try {
      const attachmentsRes = await axios.get(`${API}/worker-requests/${request.id}/attachments`);
      setRequestAttachments(attachmentsRes.data || []);
    } catch (error) {
      console.error("Failed to load attachments");
      setRequestAttachments([]);
    }
    
    setResponseDialog(true);
  };
  
  const handleFileUpload = async (files) => {
    if (!selectedRequest) return;
    
    setUploadingFiles(true);
    setUploadProgress(0);
    
    const totalFiles = files.length;
    let uploaded = 0;
    const newAttachments = [];
    
    for (const file of files) {
      try {
        // Convert file to base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64String = reader.result.split(",")[1];
            resolve(base64String);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        
        // Upload to backend
        const response = await axios.post(`${API}/worker-requests/${selectedRequest.id}/attachments`, {
          filename: file.name,
          content_type: file.type,
          data: base64,
          description: ""
        });
        
        newAttachments.push(response.data);
        uploaded++;
        setUploadProgress(Math.round((uploaded / totalFiles) * 100));
        
      } catch (error) {
        toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || "Unknown error"}`);
      }
    }
    
    if (newAttachments.length > 0) {
      setRequestAttachments([...requestAttachments, ...newAttachments]);
      toast.success(`${newAttachments.length} file(s) uploaded successfully`);
    }
    
    setUploadingFiles(false);
    setUploadProgress(0);
  };
  
  const handleAttachmentDelete = (attachmentId) => {
    setRequestAttachments(requestAttachments.filter(a => a.id !== attachmentId));
  };

  const openAssignDialog = (request) => {
    setSelectedRequest(request);
    setSelectedWorkers(request.assigned_workers || []);
    // Track original state for comparison
    setStagedChanges({
      originalWorkers: request.assigned_workers || [],
      toAdd: [],
      toRemove: []
    });
    setAssignDialog(true);
  };

  // State for staged changes
  const [stagedChanges, setStagedChanges] = useState({
    originalWorkers: [],
    toAdd: [],
    toRemove: []
  });
  const [confirmingChanges, setConfirmingChanges] = useState(false);

  const openQuoteStatusDialog = (request) => {
    setSelectedQuoteRequest(request);
    setQuoteStatusDialog(true);
  };

  const handleUpdateQuoteStatus = async (status) => {
    if (!selectedQuoteRequest?.latest_quote_id) {
      toast.error("No quote found for this request");
      return;
    }
    try {
      // Get quotes for this request and update the latest one
      const quotesRes = await axios.get(`${API}/quotes?request_id=${selectedQuoteRequest.id}`);
      if (quotesRes.data.length > 0) {
        await axios.put(`${API}/quotes/${quotesRes.data[0].id}`, { status });
        toast.success(`Quote marked as ${status}`);
        setQuoteStatusDialog(false);
        fetchRequests();
      }
    } catch (error) {
      toast.error("Failed to update quote status");
    }
  };

  const handleRespond = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/worker-requests/${selectedRequest.id}`, responseData);
      toast.success("Response sent successfully");
      setResponseDialog(false);
      fetchRequests();
    } catch (error) {
      toast.error("Failed to send response");
    }
  };

  // Stage a worker add/remove (no API call yet)
  const handleStageWorkerChange = (workerId, workerName, currentlyAssigned) => {
    const { originalWorkers, toAdd, toRemove } = stagedChanges;
    const wasOriginallyAssigned = originalWorkers.includes(workerId);
    
    if (currentlyAssigned) {
      // Currently shown as assigned - user wants to remove
      if (wasOriginallyAssigned) {
        // Was originally assigned, now staged for removal
        setStagedChanges({
          ...stagedChanges,
          toRemove: [...toRemove, { id: workerId, name: workerName }],
          toAdd: toAdd.filter(w => w.id !== workerId)
        });
      } else {
        // Was staged for add, now unstaging
        setStagedChanges({
          ...stagedChanges,
          toAdd: toAdd.filter(w => w.id !== workerId)
        });
      }
      setSelectedWorkers(selectedWorkers.filter(id => id !== workerId));
    } else {
      // Currently not assigned - user wants to add
      if (wasOriginallyAssigned) {
        // Was originally assigned but staged for removal, now unstaging
        setStagedChanges({
          ...stagedChanges,
          toRemove: toRemove.filter(w => w.id !== workerId)
        });
      } else {
        // New addition
        setStagedChanges({
          ...stagedChanges,
          toAdd: [...toAdd, { id: workerId, name: workerName }]
        });
      }
      setSelectedWorkers([...selectedWorkers, workerId]);
    }
  };

  // Check if there are pending changes
  const hasPendingChanges = () => {
    return stagedChanges.toAdd.length > 0 || stagedChanges.toRemove.length > 0;
  };

  // Apply all staged changes at once
  const handleConfirmChanges = async () => {
    const { toAdd, toRemove } = stagedChanges;
    
    if (!hasPendingChanges()) {
      setAssignDialog(false);
      return;
    }
    
    setConfirmingChanges(true);
    try {
      // Process all additions
      for (const worker of toAdd) {
        await axios.post(`${API}/worker-requests/${selectedRequest.id}/assign-worker`, {
          worker_id: worker.id,
          action: "add"
        });
      }
      
      // Process all removals
      for (const worker of toRemove) {
        await axios.post(`${API}/worker-requests/${selectedRequest.id}/assign-worker`, {
          worker_id: worker.id,
          action: "remove"
        });
      }
      
      const messages = [];
      if (toAdd.length > 0) messages.push(`${toAdd.length} worker(s) notified of assignment`);
      if (toRemove.length > 0) messages.push(`${toRemove.length} worker(s) notified of removal`);
      
      toast.success(messages.join(", "));
      setAssignDialog(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply changes");
    } finally {
      setConfirmingChanges(false);
    }
  };

  // Cancel and discard all staged changes
  const handleCancelChanges = () => {
    setSelectedWorkers(stagedChanges.originalWorkers);
    setStagedChanges({
      originalWorkers: [],
      toAdd: [],
      toRemove: []
    });
    setAssignDialog(false);
  };

  const handleDownloadQuote = async (request) => {
    setDownloadingQuote(request.id);
    try {
      const response = await axios.get(`${API}/worker-requests/${request.id}/generate-quote`, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `offert_${request.agreement_a_number || 'quote'}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.setAttribute('download', filename.replace(/\//g, '-'));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Quote PDF downloaded");
    } catch (error) {
      toast.error("Failed to generate quote. Please ensure company rates are configured.");
    } finally {
      setDownloadingQuote(null);
    }
  };
  
  const openQuotePreview = async (request) => {
    setSelectedRequest(request);
    setQuoteWorkerType("technical");
    setQuotePreview(null);
    setQuotePreviewDialog(true);
    await loadQuotePreview(request.id, "technical");
  };
  
  const loadQuotePreview = async (requestId, workerType) => {
    setLoadingPreview(true);
    try {
      const response = await axios.post(`${API}/quotes/preview`, {
        request_id: requestId,
        worker_type: workerType,
        validity_days: 30,
        notes: ""
      });
      setQuotePreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview quote");
    } finally {
      setLoadingPreview(false);
    }
  };
  
  const handleWorkerTypeChange = async (newType) => {
    setQuoteWorkerType(newType);
    if (selectedRequest) {
      await loadQuotePreview(selectedRequest.id, newType);
    }
  };
  
  const generateQuoteFromPreview = async () => {
    if (!selectedRequest) return;
    
    setQuotePreviewDialog(false);
    await handleDownloadQuote(selectedRequest);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500 text-white",
      confirmed: "bg-blue-500 text-white",
      workers_assigned: "bg-green-500 text-white",
      declined: "bg-red-500 text-white",
      completed: "bg-gray-500 text-white"
    };
    return styles[status] || "bg-gray-500 text-white";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <AlertCircle className="w-4 h-4" />;
      case 'confirmed': return <CheckCircle className="w-4 h-4" />;
      case 'workers_assigned': return <Users className="w-4 h-4" />;
      case 'declined': return <XCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  return (
    <div data-testid="contractor-requests">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-[#333333]">TEAM MEMBER REQUESTS</h1>
        <p className="text-[#52525B]">Requests from Aggreko Sweden AB</p>
      </div>

      {/* Universal Search & Filters Section */}
      <div className="flex flex-wrap gap-4 mb-4 items-end">
        <div className="flex-1 min-w-[250px] max-w-[400px]">
          <Label className="text-xs text-gray-500 uppercase mb-1 block">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by A-number, PO, project, worker, location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 pl-9"
              data-testid="universal-search"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="without-po"
            checked={filterWithoutPO}
            onCheckedChange={setFilterWithoutPO}
            data-testid="filter-without-po"
          />
          <Label htmlFor="without-po" className="text-sm cursor-pointer">
            Show only without PO
          </Label>
        </div>
        {(searchQuery || filterWithoutPO) && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => {
              setSearchQuery("");
              setFilterWithoutPO(false);
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Requests Cards */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
          </div>
        ) : filteredRequests.length > 0 ? (
          filteredRequests.map((req) => (
            <Card 
              key={req.id} 
              className={`${req.status === 'pending' ? 'border-l-4 border-l-yellow-500' : ''} cursor-pointer hover:shadow-lg transition-shadow`}
              onClick={() => openViewDialog(req)}
            >
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className={getStatusBadge(req.status)}>
                        {getStatusIcon(req.status)}
                        <span className="ml-1">{req.status?.replace('_', ' ')}</span>
                      </Badge>
                      <span className="text-sm text-gray-500">
                        Requested by {req.created_by_name}
                        {req.created_at && (
                          <span className="ml-2">on {req.created_at.split('T')[0]}</span>
                        )}
                      </span>
                    </div>

                    {/* A-Number Info */}
                    {req.agreement_a_number && (
                      <div className="bg-orange-50 p-3 rounded-lg mb-4">
                        <p className="text-xs text-orange-600 uppercase">A-Number</p>
                        <p className="text-lg font-bold text-[#F37021]">{req.agreement_a_number}</p>
                        <p className="text-sm text-orange-700">{req.agreement_project_name}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {req.is_direct_booking && (
                            <Badge className="bg-green-100 text-green-700 border border-green-300">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Direct Booking (Admin Assigned)
                            </Badge>
                          )}
                          {req.is_backdated && (
                            <Badge data-testid="badge-backdated" className="bg-blue-100 text-blue-700 border border-blue-300">
                              <Clock className="w-3 h-3 mr-1" />
                              Backdated Assignment
                            </Badge>
                          )}
                          {/* PO Number - Read only for contractor */}
                          {req.po_number ? (
                            <Badge className="bg-gray-100 text-gray-700 border border-gray-300 font-mono">
                              PO: {req.po_number}
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 border border-amber-300">
                              Pending PO
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Project Info */}
                    {req.project_name && (
                      <div className="bg-blue-50 p-3 rounded-lg mb-4">
                        <p className="text-xs text-blue-600 uppercase">Project</p>
                        <p className="text-lg font-bold text-blue-900">{req.project_name}</p>
                        <p className="text-sm text-blue-700">#{req.project_number}</p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Team Members Needed</p>
                        <p className="text-2xl font-bold text-[#F37021]">{req.num_workers}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Days</p>
                        <p className="text-2xl font-bold">{req.num_days}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Start Date</p>
                        <p className="text-lg font-medium">{req.start_date}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Location</p>
                        <p className="text-lg font-medium">{req.location_name || '-'}</p>
                      </div>
                    </div>

                    {/* Address */}
                    {req.address && (
                      <div className="mb-3">
                        <p className="text-xs text-gray-500 uppercase">Address</p>
                        <p className="text-sm">{req.address}</p>
                      </div>
                    )}

                    {/* Contact Persons */}
                    {(req.contact_person_onsite_name || req.project_engineer_name) && (
                      <div className="bg-gray-50 p-3 rounded-lg mb-4">
                        <p className="text-xs text-gray-500 uppercase mb-2">Contacts</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {req.contact_person_onsite_name && (
                            <div>
                              <p className="text-xs text-gray-500">Contact Person Onsite</p>
                              <p className="font-medium">{req.contact_person_onsite_name}</p>
                              {req.contact_person_onsite_phone && (
                                <p className="text-sm text-gray-600">{req.contact_person_onsite_phone}</p>
                              )}
                            </div>
                          )}
                          {req.project_engineer_name && (
                            <div>
                              <p className="text-xs text-gray-500">Project Engineer</p>
                              <p className="font-medium">{req.project_engineer_name}</p>
                              {req.project_engineer_phone && (
                                <p className="text-sm text-gray-600">{req.project_engineer_phone}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {req.description && (
                      <p className="text-sm text-gray-600 mb-3">{req.description}</p>
                    )}

                    {/* Requirements */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="text-sm font-medium text-gray-500">Requirements:</span>
                      {req.requirements?.id06_card && (
                        <Badge variant="outline" className="bg-blue-50">ID06 Card</Badge>
                      )}
                      {req.requirements?.identification && (
                        <Badge variant="outline" className="bg-blue-50">ID</Badge>
                      )}
                      {req.requirements?.ssg_card && (
                        <Badge variant="outline" className="bg-blue-50">SSG Card</Badge>
                      )}
                      {req.requirements?.induction_required && (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700">
                          Induction: {req.requirements.induction_details?.induction_type}
                        </Badge>
                      )}
                      {req.requirements?.other_requirements && (
                        <Badge variant="outline" className="bg-gray-50">
                          {req.requirements.other_requirements}
                        </Badge>
                      )}
                      {!req.requirements?.id06_card && !req.requirements?.identification && 
                       !req.requirements?.ssg_card && !req.requirements?.induction_required &&
                       !req.requirements?.other_requirements && (
                        <span className="text-sm text-gray-400">None specified</span>
                      )}
                    </div>

                    {/* Induction Link if provided */}
                    {req.requirements?.induction_required && req.requirements?.induction_details?.induction_link && (
                      <div className="mb-3">
                        <a 
                          href={req.requirements.induction_details.induction_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                        >
                          <FileText className="w-3 h-3" />
                          Induction Link
                        </a>
                      </div>
                    )}

                    {/* Contractor Response */}
                    {req.contractor_response && (
                      <div className="bg-green-50 p-3 rounded-lg mt-3">
                        <p className="text-sm font-medium text-green-700">Your Response:</p>
                        <p className="text-sm text-green-600">{req.contractor_response}</p>
                      </div>
                    )}

                    {/* Assigned Workers with Confirmation Status */}
                    {req.assigned_worker_names && req.assigned_worker_names.length > 0 && (
                      <div className="bg-purple-50 p-3 rounded-lg mt-3">
                        <p className="text-sm font-medium text-purple-700 mb-2">Assigned Team Members:</p>
                        <div className="space-y-2">
                          {(req.worker_confirmations || []).map((conf) => (
                            <div key={conf.worker_id || conf.worker_name} className="flex items-center justify-between bg-white p-2 rounded border">
                              <span className="font-medium">{conf.worker_name}</span>
                              {conf.confirmed ? (
                                <span className="flex items-center text-green-600 text-sm">
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Confirmed {conf.confirmed_at ? new Date(conf.confirmed_at).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                                </span>
                              ) : (
                                <span className="flex items-center text-amber-600 text-sm">
                                  <Clock className="w-4 h-4 mr-1" />
                                  Not confirmed yet
                                </span>
                              )}
                            </div>
                          ))}
                          {/* Fallback if no confirmation data but has names */}
                          {(!req.worker_confirmations || req.worker_confirmations.length === 0) && req.assigned_worker_names.map((name) => (
                            <div key={name} className="flex items-center justify-between bg-white p-2 rounded border">
                              <span className="font-medium">{name}</span>
                              <span className="flex items-center text-gray-400 text-sm">
                                <Clock className="w-4 h-4 mr-1" />
                                Status unknown
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Quote Status */}
                    {req.latest_quote_number && (
                      <div className="bg-gray-50 p-3 rounded-lg mt-3 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Quote</p>
                            <p className="font-mono font-bold text-[#F37021]">{req.latest_quote_number}</p>
                          </div>
                          <div className="text-right">
                            <Badge className={
                              req.latest_quote_status === 'accepted' ? 'bg-green-100 text-green-800' :
                              req.latest_quote_status === 'rejected' ? 'bg-red-100 text-red-800' :
                              req.latest_quote_status === 'expired' ? 'bg-gray-100 text-gray-600' :
                              req.latest_quote_status === 'sent' ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                            }>
                              {req.latest_quote_status || 'draft'}
                            </Badge>
                            {req.latest_quote_valid_until && (
                              <p className="text-xs text-gray-500 mt-1">Valid until: {req.latest_quote_valid_until}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                    {req.status === 'pending' && (
                      <>
                        <Button 
                          className="btn-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            openResponseDialog(req);
                          }}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Respond
                        </Button>
                      </>
                    )}
                    {(req.status === 'confirmed' || req.status === 'workers_assigned') && (
                      <Button 
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          openAssignDialog(req);
                        }}
                        data-testid={`assign-workers-${req.id}`}
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Assign Team Members
                      </Button>
                    )}
                    <Button 
                      variant="outline"
                      className="border-green-300 text-green-700 hover:bg-green-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        openQuotePreview(req);
                      }}
                      data-testid={`preview-quote-${req.id}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview Quote
                    </Button>
                    {req.latest_quote_number && (
                      <Button 
                        variant="outline"
                        className="border-orange-300 text-orange-700 hover:bg-orange-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          openQuoteStatusDialog(req);
                        }}
                        data-testid={`quote-status-${req.id}`}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Update Quote Status
                      </Button>
                    )}
                    {req.status !== 'pending' && (
                      <Button 
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          openResponseDialog(req);
                        }}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Update Status
                      </Button>
                    )}
                    <Button 
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRequest(req.id);
                      }}
                      data-testid={`delete-request-${req.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Request
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="p-12 text-center text-gray-400">
              No team member requests yet
            </CardContent>
          </Card>
        )}
      </div>

      {/* Response Dialog */}
      <Dialog open={responseDialog} onOpenChange={setResponseDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl">RESPOND TO REQUEST</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRespond} className="space-y-4">
            {selectedRequest && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium">{selectedRequest.num_workers} team members for {selectedRequest.num_days} days</p>
                <p className="text-sm text-gray-500">{selectedRequest.location} - Starting {selectedRequest.start_date}</p>
              </div>
            )}

            <div>
              <Label className="input-label">Status</Label>
              <Select
                value={responseData.status}
                onValueChange={(value) => setResponseData({ ...responseData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Confirmed - Can provide team members</SelectItem>
                  <SelectItem value="workers_assigned">Team Members Assigned</SelectItem>
                  <SelectItem value="declined">Declined - Cannot fulfill</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="input-label">Response Message</Label>
              <Textarea
                value={responseData.contractor_response}
                onChange={(e) => setResponseData({ ...responseData, contractor_response: e.target.value })}
                placeholder="Add details about team members, availability, or any notes..."
                rows={4}
              />
            </div>

            {/* File Attachments Section */}
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-[#F37021]" />
                <Label className="input-label">Attachments (Quotes, certificates, documents)</Label>
              </div>
              
              <FileUploadZone
                onUpload={handleFileUpload}
                uploading={uploadingFiles}
                progress={uploadProgress}
                multiple={true}
              />
              
              {requestAttachments.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Attached Files ({requestAttachments.length})
                  </p>
                  <AttachmentList
                    attachments={requestAttachments}
                    onDelete={handleAttachmentDelete}
                    canDelete={true}
                    currentUserId={user?.id}
                    showUploader={true}
                  />
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button type="submit" className="flex-1 btn-primary">
                <Send className="w-4 h-4 mr-2" />
                Send Response
              </Button>
              <Button type="button" variant="outline" onClick={() => setResponseDialog(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">REQUEST DETAILS</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center gap-3">
                <Badge className={getStatusBadge(selectedRequest.status)}>
                  {getStatusIcon(selectedRequest.status)}
                  <span className="ml-1">{selectedRequest.status?.replace('_', ' ')}</span>
                </Badge>
                <span className="text-sm text-gray-500">
                  Requested by {selectedRequest.created_by_name}
                </span>
              </div>

              {/* Project Info */}
              {selectedRequest.project_name && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-xs text-blue-600 uppercase mb-1">Project</p>
                  <p className="text-xl font-bold text-blue-900">{selectedRequest.project_name}</p>
                  <p className="text-sm text-blue-700">#{selectedRequest.project_number}</p>
                </div>
              )}

              {/* Main Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Team Members Needed</p>
                  <p className="text-3xl font-bold text-[#F37021]">{selectedRequest.num_workers}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Number of Days</p>
                  <p className="text-3xl font-bold">{selectedRequest.num_days}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Start Date</p>
                  <p className="text-xl font-medium">{selectedRequest.start_date}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Location</p>
                  <p className="text-xl font-medium">{selectedRequest.location_name || '-'}</p>
                </div>
              </div>

              {/* Purchase Order (PO) - Read only for contractor */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-500 uppercase mb-1">Purchase Order (PO)</p>
                {selectedRequest.po_number ? (
                  <p className="text-lg font-mono font-bold text-gray-800">{selectedRequest.po_number}</p>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700">Pending PO from Aggreko</Badge>
                )}
                {selectedRequest.po_added_at && (
                  <p className="text-xs text-gray-400 mt-1">
                    Added by {selectedRequest.po_added_by_name} on {new Date(selectedRequest.po_added_at).toLocaleDateString()}
                  </p>
                )}
              </div>

              {/* Address */}
              {selectedRequest.address && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase mb-1">Full Address</p>
                  <p className="text-lg">{selectedRequest.address}</p>
                </div>
              )}

              {/* Contact Persons */}
              {(selectedRequest.contact_person_onsite_name || selectedRequest.project_engineer_name) && (
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-xs text-orange-600 uppercase mb-3">Contact Information</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedRequest.contact_person_onsite_name && (
                      <div>
                        <p className="text-xs text-gray-500">Contact Person Onsite</p>
                        <p className="text-lg font-medium">{selectedRequest.contact_person_onsite_name}</p>
                        {selectedRequest.contact_person_onsite_phone && (
                          <p className="text-sm text-gray-600">{selectedRequest.contact_person_onsite_phone}</p>
                        )}
                      </div>
                    )}
                    {selectedRequest.project_engineer_name && (
                      <div>
                        <p className="text-xs text-gray-500">Project Engineer</p>
                        <p className="text-lg font-medium">{selectedRequest.project_engineer_name}</p>
                        {selectedRequest.project_engineer_phone && (
                          <p className="text-sm text-gray-600">{selectedRequest.project_engineer_phone}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedRequest.description && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase mb-1">Description</p>
                  <p className="text-base">{selectedRequest.description}</p>
                </div>
              )}

              {/* Requirements */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-500 uppercase mb-2">Requirements</p>
                <div className="flex flex-wrap gap-2">
                  {selectedRequest.requirements?.id06_card && (
                    <Badge className="bg-blue-500 text-white">ID06 Card</Badge>
                  )}
                  {selectedRequest.requirements?.identification && (
                    <Badge className="bg-blue-500 text-white">ID (Identification)</Badge>
                  )}
                  {selectedRequest.requirements?.ssg_card && (
                    <Badge className="bg-blue-500 text-white">SSG Card</Badge>
                  )}
                  {selectedRequest.requirements?.induction_required && (
                    <Badge className="bg-orange-500 text-white">
                      Induction: {selectedRequest.requirements.induction_details?.induction_type}
                    </Badge>
                  )}
                  {selectedRequest.requirements?.other_requirements && (
                    <Badge className="bg-gray-500 text-white">
                      {selectedRequest.requirements.other_requirements}
                    </Badge>
                  )}
                  {!selectedRequest.requirements?.id06_card && 
                   !selectedRequest.requirements?.identification && 
                   !selectedRequest.requirements?.ssg_card && 
                   !selectedRequest.requirements?.induction_required &&
                   !selectedRequest.requirements?.other_requirements && (
                    <span className="text-gray-400">No specific requirements</span>
                  )}
                </div>

                {/* Induction Details */}
                {selectedRequest.requirements?.induction_required && selectedRequest.requirements?.induction_details && (
                  <div className="mt-4 p-3 bg-white rounded border">
                    <p className="text-sm font-medium mb-2">Induction Details:</p>
                    {selectedRequest.requirements.induction_details.when_needed && (
                      <p className="text-sm">Needed by: {selectedRequest.requirements.induction_details.when_needed}</p>
                    )}
                    {selectedRequest.requirements.induction_details.induction_link && (
                      <p className="text-sm">
                        <a href={selectedRequest.requirements.induction_details.induction_link} 
                           target="_blank" rel="noopener noreferrer"
                           className="text-blue-600 hover:underline">
                          Induction Link
                        </a>
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Attachments Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Paperclip className="w-4 h-4 text-[#F37021]" />
                  <p className="text-xs text-gray-500 uppercase">
                    Attachments ({requestAttachments.length})
                  </p>
                </div>
                
                {requestAttachments.length > 0 ? (
                  <AttachmentList
                    attachments={requestAttachments}
                    onDelete={handleAttachmentDelete}
                    canDelete={true}
                    currentUserId={user?.id}
                    showUploader={true}
                  />
                ) : (
                  <p className="text-sm text-gray-400 italic">No files attached to this request.</p>
                )}
              </div>

              {/* Your Response (if any) */}
              {selectedRequest.contractor_response && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-xs text-green-600 uppercase mb-1">Your Response</p>
                  <p className="text-base">{selectedRequest.contractor_response}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4">
                <Button 
                  className="flex-1 btn-primary"
                  onClick={() => {
                    setViewDialog(false);
                    openResponseDialog(selectedRequest);
                  }}
                >
                  {selectedRequest.status === 'pending' ? 'Respond to Request' : 'Update Response'}
                </Button>
                <Button variant="outline" onClick={() => setViewDialog(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Workers Dialog - Staged Changes (no emails until confirmed) */}
      <Dialog open={assignDialog} onOpenChange={(open) => {
        if (!open && hasPendingChanges()) {
          // If closing with pending changes, ask to confirm
          if (window.confirm("You have unsaved changes. Discard them?")) {
            handleCancelChanges();
          }
        } else if (!open) {
          setAssignDialog(false);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl">ASSIGN TEAM MEMBERS</DialogTitle>
            <p className="text-sm text-gray-500">Click workers to stage changes. No notifications sent until you confirm.</p>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <p className="font-bold text-[#F37021]">{selectedRequest.agreement_a_number || 'Request'}</p>
                <p className="text-sm text-orange-700">{selectedRequest.num_workers} team member(s) requested for {selectedRequest.num_days} day(s)</p>
                <p className="text-sm text-orange-600">Starting: {selectedRequest.start_date}</p>
              </div>

              <div>
                <Label className="input-label mb-3 block">Click to Add/Remove Workers:</Label>
                {workers.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No manpower team members available. Add team members first.</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {workers.filter(w => w.user_type === 'worker').map((worker) => {
                      const isAssigned = selectedWorkers.includes(worker.id);
                      const isPendingAdd = stagedChanges.toAdd.some(w => w.id === worker.id);
                      const isPendingRemove = stagedChanges.toRemove.some(w => w.id === worker.id);
                      
                      return (
                        <div 
                          key={worker.id}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            isAssigned
                              ? isPendingAdd 
                                ? "border-green-500 bg-green-50" // New addition
                                : "border-[#F37021] bg-orange-50" // Already assigned
                              : isPendingRemove
                                ? "border-red-300 bg-red-50" // Staged for removal
                                : "border-gray-200 hover:border-gray-300"
                          }`}
                          onClick={() => handleStageWorkerChange(worker.id, worker.name, isAssigned)}
                          data-testid={`worker-option-${worker.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{worker.name}</p>
                              <p className="text-sm text-gray-500">{worker.email}</p>
                              {isPendingAdd && (
                                <p className="text-xs text-green-600 font-medium mt-1">+ Will be notified of assignment</p>
                              )}
                              {isPendingRemove && (
                                <p className="text-xs text-red-600 font-medium mt-1">- Will be notified of removal</p>
                              )}
                            </div>
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              isAssigned
                                ? isPendingAdd
                                  ? "border-green-500 bg-green-500"
                                  : "border-[#F37021] bg-[#F37021]"
                                : "border-gray-300"
                            }`}>
                              {isAssigned ? (
                                <CheckCircle className="w-4 h-4 text-white" />
                              ) : (
                                <UserPlus className="w-3 h-3 text-gray-400" />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Pending Changes Summary */}
              {hasPendingChanges() && (
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                  <p className="font-medium text-yellow-800 mb-2">Pending Changes:</p>
                  {stagedChanges.toAdd.length > 0 && (
                    <p className="text-sm text-green-700">
                      {stagedChanges.toAdd.length} worker(s) will be notified they are assigned: {stagedChanges.toAdd.map(w => w.name).join(", ")}
                    </p>
                  )}
                  {stagedChanges.toRemove.length > 0 && (
                    <p className="text-sm text-red-700 mt-1">
                      {stagedChanges.toRemove.length} worker(s) will be notified they are no longer needed: {stagedChanges.toRemove.map(w => w.name).join(", ")}
                    </p>
                  )}
                </div>
              )}

              {!hasPendingChanges() && (
                <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
                  <p>No changes staged. Click workers to add or remove them.</p>
                  <p className="mt-1 text-xs">Currently assigned: {stagedChanges.originalWorkers.length > 0 
                    ? workers.filter(w => stagedChanges.originalWorkers.includes(w.id)).map(w => w.name).join(", ")
                    : "None"}</p>
                </div>
              )}

              <div className="flex gap-2">
                {hasPendingChanges() ? (
                  <>
                    <Button 
                      onClick={handleConfirmChanges}
                      className="flex-1 btn-primary"
                      disabled={confirmingChanges}
                      data-testid="confirm-assign-changes"
                    >
                      {confirmingChanges ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Confirm & Send Notifications
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleCancelChanges}
                      disabled={confirmingChanges}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" className="flex-1" onClick={() => setAssignDialog(false)}>
                    Close
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quote Status Dialog */}
      <Dialog open={quoteStatusDialog} onOpenChange={setQuoteStatusDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Quote Status</DialogTitle>
          </DialogHeader>
          {selectedQuoteRequest && (
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <p className="font-mono font-bold text-[#F37021]">{selectedQuoteRequest.latest_quote_number}</p>
                <p className="text-sm text-orange-700">{selectedQuoteRequest.agreement_a_number}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => handleUpdateQuoteStatus('sent')}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Sent
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => handleUpdateQuoteStatus('accepted')}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Accepted
                </Button>
                <Button 
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => handleUpdateQuoteStatus('rejected')}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rejected
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setQuoteStatusDialog(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quote Preview Dialog */}
      <Dialog open={quotePreviewDialog} onOpenChange={setQuotePreviewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">QUOTE PREVIEW</DialogTitle>
            <p className="text-sm text-gray-500">
              Review pricing breakdown before generating PDF. Rates are locked at current values.
            </p>
          </DialogHeader>
          
          {loadingPreview ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
            </div>
          ) : quotePreview ? (
            <div className="space-y-4">
              {/* Request Info */}
              <div className="bg-orange-50 p-4 rounded-lg">
                <p className="font-bold text-[#F37021] text-lg">{quotePreview.agreement_a_number}</p>
                <p className="text-orange-700">{quotePreview.agreement_project_name}</p>
                <div className="flex gap-4 mt-2 text-sm text-orange-600">
                  <span>{quotePreview.num_workers} workers</span>
                  <span>{quotePreview.start_date} - {quotePreview.end_date || 'TBD'}</span>
                </div>
              </div>
              
              {/* Worker Type Selection */}
              <div className="p-4 border rounded-lg">
                <Label className="input-label mb-3 block">Worker Type</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="workerType"
                      checked={quoteWorkerType === "technical"}
                      onChange={() => handleWorkerTypeChange("technical")}
                      className="w-4 h-4 text-[#F37021]"
                    />
                    <span className="text-sm">Technical Staff</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="workerType"
                      checked={quoteWorkerType === "labour"}
                      onChange={() => handleWorkerTypeChange("labour")}
                      className="w-4 h-4 text-[#F37021]"
                    />
                    <span className="text-sm">Labour</span>
                  </label>
                </div>
              </div>
              
              {/* Day Breakdown */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-3">Day Breakdown</p>
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div>
                    <p className="text-2xl font-bold">{quotePreview.total_days}</p>
                    <p className="text-xs text-gray-500">Total Days</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{quotePreview.normal_days}</p>
                    <p className="text-xs text-gray-500">Normal</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{quotePreview.weekend_days}</p>
                    <p className="text-xs text-gray-500">Weekend</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">{quotePreview.holiday_days}</p>
                    <p className="text-xs text-gray-500">Holiday</p>
                  </div>
                </div>
                {quotePreview.work_on_weekends && (
                  <p className="text-xs text-amber-600 mt-2 text-center">
                    Includes weekend/holiday work at holiday rate
                  </p>
                )}
              </div>
              
              {/* Rates */}
              <div className="flex gap-4">
                <div className="flex-1 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-xs text-green-600 uppercase">Standard Rate</p>
                  <p className="text-lg font-bold text-green-700">
                    {quotePreview.daily_rate?.toLocaleString('sv-SE')} SEK/day
                  </p>
                </div>
                {(quotePreview.weekend_days > 0 || quotePreview.holiday_days > 0) && (
                  <div className="flex-1 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-600 uppercase">Holiday Rate</p>
                    <p className="text-lg font-bold text-amber-700">
                      {quotePreview.holiday_rate?.toLocaleString('sv-SE')} SEK/day
                    </p>
                  </div>
                )}
              </div>
              
              {/* Line Items Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium">Description</th>
                      <th className="text-right p-3 text-sm font-medium">Qty</th>
                      <th className="text-right p-3 text-sm font-medium">Rate</th>
                      <th className="text-right p-3 text-sm font-medium">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotePreview.line_items?.filter(Boolean).map((item) => (
                      <tr key={item.description || item.type} className="border-t">
                        <td className="p-3 text-sm">
                          {quotePreview.worker_type_label} - {item.description}
                          <br />
                          <span className="text-xs text-gray-500">
                            {quotePreview.num_workers} workers x {item.quantity / quotePreview.num_workers} days
                          </span>
                        </td>
                        <td className="p-3 text-sm text-right">{item.quantity}</td>
                        <td className="p-3 text-sm text-right">{item.rate?.toLocaleString('sv-SE')}</td>
                        <td className="p-3 text-sm text-right font-medium">
                          {item.subtotal?.toLocaleString('sv-SE')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Totals */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Net</span>
                    <span>{quotePreview.subtotal?.toLocaleString('sv-SE')} SEK</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>VAT ({quotePreview.vat_percent}%)</span>
                    <span>{quotePreview.vat_amount?.toLocaleString('sv-SE')} SEK</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Total</span>
                    <span className="text-[#F37021]">{quotePreview.total?.toLocaleString('sv-SE')} SEK</span>
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button 
                  className="flex-1 btn-primary"
                  onClick={generateQuoteFromPreview}
                  disabled={downloadingQuote}
                >
                  {downloadingQuote ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Generate & Download PDF
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setQuotePreviewDialog(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">Failed to load preview</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractorRequests;
